import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ExcelJS from "exceljs";
import userService from "reduxStore/service/userService";
import { getRoleList } from "reduxStore/action/roleActions";
import {
  getUserFilterDetail,
  getUserList,
  userStatusUpdate,
} from "reduxStore/action/usersActions";
import TableDataNotFound from "component/TableDataNotFound";
import TableListSkeleton from "component/common/TableListSkeleton";
import strings from "translate/language";
import moment from "moment";
import { handleListResponse, seo } from "utils/utils";
import Breadcrumb from "component/common/breadcrumb";
import Pagination from "component/common/Pagination";
import usePermission from "utils/usePermission";
import usePage from "utils/usePage";
import { ChannelPartner, CPAgent } from "utils/constants";
import { getReportPropertyList } from "reduxStore/action/reportActions";
import { getCityList } from "reduxStore/action/masterActions";
import "./UserListing.css";

const renderUserCell = (value, toneClass = "") => {
  const text =
    value === null || value === undefined || String(value).trim() === ""
      ? "-"
      : String(value).trim();
  const tone = text === "-" ? "user-listing__empty" : toneClass;
  return (
    <span className={`user-listing__cell ${tone}`.trim()}>{text}</span>
  );
};

const User = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = new URLSearchParams(window.location.search);
  const roleId = params.get("role_id");
  const [page, setPage] = usePage();
  const [endDateDisable, setEndDateDisable] = useState(true);
  const [handleStatus, setHandleStatus] = useState({});
  const [formVaild, setFormVaild] = useState({});
  const [userList, setUserList] = useState([]);
  const [isListLoading, setIsListLoading] = useState(true);
  const [rolesData, setRolesData] = useState([]);
  const [propertyList, setPropertyList] = useState([]);
  const [form, setForm] = useState({
    start_date: "",
    end_date: "",
    role_id: "",
    search_name: "",
    email: "",
    status: "",
    _id: "",
    city_id: "",
  });
  const [selectedusers, setSelectedusers] = useState([]);
  const [selectAllCheckbox, setSelectAllCheckbox] = useState(false);
  const [exporting, setExporting] = useState(false);
  const pendingListFetch = React.useRef(false);
  const userListSelector = useSelector((state) => state?.userData) || [];
  const getRoleListSelector = useSelector((state) => state.roleData) || {};
  const ReportListSelector = useSelector((state) => state.report);
  const GetCityListSelector =
    useSelector((state) => state.cityMasterData?.response?.data) || [];

  const { create, view, edit } = usePermission({
    create: "add_user",
    view: "user_detail",
    edit: "edit_user",
  });
  const navigatpage = async (navname) => {
    navigate(navname);
  };

  const requestUserList = (params) => {
    pendingListFetch.current = true;
    setUserList([]);
    setIsListLoading(true);
    dispatch(getUserFilterDetail(params));
  };

  useEffect(() => {
    seo({
      title: strings.userManagement,
      metaDescription: strings.userListing,
      canonicalLink: window.location.href,
    });
  }, []);

  useEffect(() => {
    const { reportPropertyList } = ReportListSelector;
    if (reportPropertyList) {
      if (reportPropertyList?.status === 200) {
        setPropertyList(handleListResponse(reportPropertyList.data));
      } else {
        setPropertyList([]);
      }
    }
  }, [ReportListSelector]);

  useEffect(() => {
    requestUserList({ limit: 10, offset: page, role_id: roleId });
  }, [dispatch]);

  useEffect(() => {
    const { response } = getRoleListSelector;
    if (response) {
      if (response?.status === 200) {
        let filterData = handleListResponse(response.data).filter(
          (i) => i._id !== CPAgent && i._id !== ChannelPartner
        );
        setRolesData(filterData);
      } else {
        setRolesData([]);
      }
    }
  }, [getRoleListSelector]);

  useEffect(() => {
    selectAllCheckBoxVal();
  }, []);

  const handlePageClick = (newOffset) => {
    window.scroll(0, 0);
    setPage(newOffset);
    requestUserList({
      limit: 10,
      offset: newOffset,
      start_date: form.start_date,
      end_date: form.end_date,
      role_id: roleId ? roleId : form.role_id,
      search_name: form.search_name,
      email: form.email,
      status: form.status,
      property_id: form?.site?._id,
      city_id: form.city_id,
    });
  };

  const filterDetail = () => {
    if (!handleValid()) {
      setPage(0);
      requestUserList({
        limit: 10,
        offset: 0,
        start_date: form.start_date,
        end_date: form.end_date,
        role_id: roleId ? roleId : form.role_id,
        search_name: form.search_name,
        email: form.email,
        status: form.status,
        property_id: form?.site?._id,
        city_id: form.city_id,
        filtertype: true,
      });
    }
  };

  const buildUserFilterParams = (extra = {}) => ({
    start_date: form.start_date,
    end_date: form.end_date,
    role_id: roleId ? roleId : form.role_id,
    search_name: form.search_name,
    email: form.email,
    status: form.status,
    property_id: form?.site?._id,
    city_id: form.city_id,
    filtertype: true,
    ...extra,
  });

  const exportToExcel = async () => {
    try {
      setExporting(true);
      const exportParams = buildUserFilterParams();
      const res = await userService.getUserFilterDetail(exportParams);
      if (res?.data?.status !== 200) {
        return;
      }
      const rows = (res?.data?.data || []).filter(
        (item) => item.role_title !== "Super Admin"
      );

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Users");
      worksheet.columns = [
        { header: "Sr.No", key: "srno", width: 8 },
        { header: "Name", key: "name", width: 25 },
        { header: "Created Date", key: "createdDate", width: 18 },
        { header: "Contact No", key: "contact", width: 16 },
        { header: "Email", key: "email", width: 28 },
        { header: "Status", key: "status", width: 12 },
        { header: "Role", key: "role", width: 20 },
      ];

      worksheet.addRows(
        rows.map((item, index) => ({
          srno: index + 1,
          name: `${item.firstname || ""} ${item.lastname || ""}`.trim(),
          createdDate: item.created_date
            ? moment(item.created_date).format("DD-MM-YYYY")
            : "",
          contact: item.mobile || "",
          email: item.email || "",
          status: item.status ? strings.active : strings.deactive,
          role: item.role_title || "",
        }))
      );
      worksheet.getRow(1).font = { bold: true };

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "users_export.xlsx";
      link.click();
    } catch (error) {
      console.error("User export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const filterReset = () => {
    setForm({
      start_date: "",
      end_date: "",
      role_id: "",
      search_name: "",
      email: "",
      status: "",
      site: "",
      cityObj: "",
    });
    setFormVaild({
      ...formVaild,
      end_date: "",
    });
    setPage(0);
    requestUserList({ limit: 10, offset: 0, role_id: roleId });
  };

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;
    if (form.start_date !== "") {
      if (form.end_date === "") {
        formError = true;
        err["end_date"] = strings.endDateErr;
      }
    }
    setFormVaild(err);
    return formError;
  };

  const handleInputField = (e) => {
    const nextFormState = {
      ...form,
      [e.target.name]: e.target.value,
    };
    setForm(nextFormState);
    if (e.target.name === "end_date") {
      setFormVaild({
        ...formVaild,
        end_date: "",
      });
    }
  };

  const handleUserStatus = () => {
    dispatch(userStatusUpdate(handleStatus));
  };

  useEffect(() => {
    if (userListSelector.create) {
      if (userListSelector?.userData?.status === 200) {
        if (handleStatus.user_id) {
          requestUserList({ limit: 10, offset: page });
        }
        setHandleStatus({});
      }
      return;
    }

    // Ignore USER_DETAIL payloads (single object) — they must not fill the table.
    if (userListSelector.detail) {
      return;
    }

    if (!pendingListFetch.current) {
      return;
    }

    const rows = userListSelector?.userData?.data;
    if (userListSelector?.userData?.status === 200 && Array.isArray(rows)) {
      // Listing always requests limit 10. Skip leftover unbounded filter
      // payloads (e.g. from role-change parent fetches) until the page lands.
      if (rows.length > 10) {
        return;
      }
      setUserList(handleListResponse(rows) ? rows : []);
      var areAllElementsPresent =
        rows.length &&
        rows.every((element) => selectedusers.includes(element.login_id));
      setSelectAllCheckbox(areAllElementsPresent);
      pendingListFetch.current = false;
      setIsListLoading(false);
      return;
    }

    if (userListSelector?.userData) {
      setUserList([]);
      pendingListFetch.current = false;
      setIsListLoading(false);
    }
  }, [dispatch, userListSelector]);

  useEffect(() => {
    dispatch(getRoleList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getCityList());
  }, [dispatch]);

  useEffect(() => {
    if (form.start_date === "") {
      setEndDateDisable(true);
    } else {
      setEndDateDisable(false);
    }
  }, [form.start_date]);

  const selectAllCheckBoxVal = (e) => {
    const res = e ? e.target.checked : false;
    const getIds = userList?.map((user) => user.login_id);
    if (res) {
      const arr = [...selectedusers, ...getIds];
      const uniqueIds = [...new Set(arr)];
      setSelectedusers(uniqueIds);
      setSelectAllCheckbox(true);
    } else {
      setSelectAllCheckbox(false);
      const unmatchedInArr1 = userList.filter(
        (item) => !selectedusers.includes(item.login_id)
      );
      const unmatchedInArr2 = selectedusers.filter(
        (item) => !userList.map((obj) => obj.login_id)?.includes(item)
      );
      const dd = [...unmatchedInArr1, ...unmatchedInArr2];
      setSelectedusers(dd);
    }
  };

  const handleSelectUser = (status, item, index) => {
    const checkAlreadySel = selectedusers.findIndex(
      (user) => user === item.login_id
    );
    const arr = [...selectedusers];
    if (checkAlreadySel === -1) {
      arr.push(item.login_id);
    } else {
      arr.splice(checkAlreadySel, 1);
    }
    setSelectedusers(arr);
    var areAllElementsPresent = userList.every((element) =>
      arr.includes(element.login_id)
    );
    setSelectAllCheckbox(areAllElementsPresent);
  };

  return (
    <div className="page-body justo-user-page">
      {/* Model_start */}
      <div
        className="modal fade"
        id="exampleModal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                {handleStatus.status
                  ? strings.activeUser
                  : strings.deactiveUser}
              </h5>
              <button
                className="close"
                type="button"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <div className="text-center">
                <h6>{strings.areYouSure}</h6>
              </div>
              <div className="text-center">
                <h6>
                  {handleStatus.status
                    ? strings.activeThisUser
                    : strings.deactiveThisUser}
                </h6>
              </div>
            </div>
            <div className="modal-footer justify-content-center">
              <button
                className="btn btn-success mr-5"
                type="button"
                onClick={() => handleUserStatus()}
                data-dismiss="modal"
              >
                {strings.yes}
              </button>
              <button
                className="btn btn-primary"
                type="button"
                data-dismiss="modal"
              >
                {strings.no}
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Model_end */}
      <Breadcrumb
        title={strings.userManagement}
        parentListing={null}
        titleactive={strings.userListing}
      />
      <div className="container-fluid justo-user">
        <div className="justo-user__card">
          <div className="justo-user__top">
            <div className="justo-user__title-block">
              <h1 className="justo-user__title">{strings.userListing}</h1>
              <p className="justo-user__subtitle">{strings.userListSubtitle}</p>
            </div>
            <div className="justo-user__top-actions">
              {create ? (
                <button
                  type="button"
                  className="justo-user__btn justo-user__btn--solid"
                  onClick={() => navigatpage("/addnewuser")}
                >
                  {strings.addNewUser}
                </button>
              ) : null}
              {userList?.length > 0 ? (
                <button
                  type="button"
                  className="justo-user__btn justo-user__btn--outline"
                  onClick={exportToExcel}
                  disabled={exporting}
                >
                  {exporting ? "Exporting..." : "Export to Excel"}
                </button>
              ) : null}
            </div>
          </div>

          <form
            className="justo-user__filters"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="justo-user__filters-grid">
              <label className="justo-user__field">
                <span className="justo-user__label">{strings.startDate}</span>
                <input
                  className="form-control justo-user__input"
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleInputField}
                />
              </label>
              <label className="justo-user__field">
                <span className="justo-user__label">{strings.endDate}</span>
                <input
                  className="form-control justo-user__input"
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleInputField}
                  min={form.start_date}
                  disabled={endDateDisable}
                />
                {formVaild.end_date ? (
                  <span className="justo-user__error">{formVaild.end_date}</span>
                ) : null}
              </label>
              <label className="justo-user__field">
                <span className="justo-user__label">{strings.byRole}</span>
                <select
                  className="form-control justo-user__input"
                  name="role_id"
                  value={form.role_id}
                  onChange={handleInputField}
                >
                  <option value="">{strings.selectRole}</option>
                  {rolesData.map((item, index) => (
                    <option key={index} value={item._id}>
                      {item.role_title}
                    </option>
                  ))}
                </select>
              </label>
              <label className="justo-user__field">
                <span className="justo-user__label">{strings.searchByName}</span>
                <input
                  className="form-control justo-user__input"
                  type="text"
                  name="search_name"
                  value={form.search_name}
                  onChange={handleInputField}
                  placeholder={strings.searchName}
                  autoComplete="off"
                />
              </label>
              <label className="justo-user__field">
                <span className="justo-user__label">{strings.searchByEmail}</span>
                <input
                  className="form-control justo-user__input"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputField}
                  placeholder={strings.searchEmail}
                  autoComplete="off"
                />
              </label>
              <label className="justo-user__field">
                <span className="justo-user__label">{strings.searchByStatus}</span>
                <select
                  className="form-control justo-user__input"
                  name="status"
                  value={form.status}
                  onChange={handleInputField}
                >
                  <option value="">{strings.selectstatus}</option>
                  <option value="1">{strings.active}</option>
                  <option value="2">{strings.deactive}</option>
                </select>
              </label>
            </div>
            <div className="justo-user__filter-footer">
              <div className="justo-user__filter-actions">
                <button
                  type="button"
                  className="justo-user__btn justo-user__btn--solid"
                  onClick={filterDetail}
                >
                  {strings.search}
                </button>
                <button
                  type="button"
                  className="justo-user__btn justo-user__btn--ghost"
                  onClick={filterReset}
                >
                  {strings.reset}
                </button>
              </div>
            </div>
          </form>

          <div className="user-listing__scroll">
            {isListLoading ? (
              <TableListSkeleton rows={10} columns={7} />
            ) : (
              <>
                <table className="table user-listing__table">
                  <thead>
                    <tr>
                      <th scope="col">{strings.name}</th>
                      <th scope="col">{strings.createdDate}</th>
                      <th scope="col">{strings.contactNo}</th>
                      <th scope="col">{strings.emailId}</th>
                      <th scope="col" className="user-listing__status-col">
                        {strings.status}
                      </th>
                      <th scope="col">{strings.role}</th>
                      <th scope="col" className="user-listing__actions-col">
                        {strings.action}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {userList?.length ? (
                      userList.map((item, index) => {
                        return item.role_title !== "Super Admin" ? (
                          <tr key={item._id || index}>
                            <td>
                              {renderUserCell(
                                `${item.firstname || ""} ${item.lastname || ""}`.trim(),
                                "user-listing__primary",
                              )}
                            </td>
                            <td>
                              {renderUserCell(
                                item.created_date
                                  ? moment(item.created_date).format(
                                      "DD-MM-yyyy hh:mm a",
                                    )
                                  : "",
                                "user-listing__date",
                              )}
                            </td>
                            <td>
                              {renderUserCell(item.mobile, "user-listing__muted")}
                            </td>
                            <td>
                              {renderUserCell(item.email, "user-listing__muted")}
                            </td>
                            <td className="user-listing__status-col">
                              <span
                                className={`user-status-badge ${
                                  item.status
                                    ? "user-status-badge--active"
                                    : "user-status-badge--inactive"
                                }`}
                              >
                                <i
                                  className={`fa ${
                                    item.status
                                      ? "fa-check-circle"
                                      : "fa-exclamation-circle"
                                  } user-status-badge__icon`}
                                  aria-hidden="true"
                                />
                                <span>
                                  {item.status
                                    ? strings.active
                                    : strings.deactive}
                                </span>
                              </span>
                            </td>
                            <td>
                              {renderUserCell(
                                item.role_title,
                                "user-listing__muted",
                              )}
                            </td>
                            <td className="user-listing__actions-col">
                              <div className="user-listing__actions">
                                {view ? (
                                  <button
                                    type="button"
                                    className="user-listing__action-btn"
                                    title={strings.viewUser}
                                    onClick={() => {
                                      navigatpage(`/viewuser/${item._id}`);
                                    }}
                                  >
                                    <i className="fa fa-eye" aria-hidden="true" />
                                  </button>
                                ) : (
                                  <span
                                    className="user-listing__action-btn user-listing__action-btn--placeholder"
                                    aria-hidden="true"
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ) : null;
                      })
                    ) : (
                      <tr />
                    )}
                  </tbody>
                </table>
                <TableDataNotFound data={userList} />
              </>
            )}
          </div>
          <Pagination
            totalCount={userListSelector?.userData?.total_data}
            listData={userList}
            onPageChange={handlePageClick}
            itemOffset={page}
            setItemOffset={setPage}
          />
        </div>
      </div>
    </div>
  );
};

export default User;
